import { PropsWithChildren, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';
import { Alert, DialogBody, PrimaryButton } from '@dev/ee-components';
import React from 'react';
import { Box } from '@material-ui/core';
import { CancelReshardStatusPolling, StartPollingReshardStatus, StartReshardContacts } from '@/store/contacts/actions';
import { SaveCampaignStart, getCampainCreatorStore } from '@/store/campaignCreator/actions';
import { UpdateDialog, HideDialog, ShowDialog } from '@/store/dialog/actions';
import { SendModal } from '@/components/CampaignCreator/SendModal';
import { HomePaths } from '../dictionaries/Paths';
import { StorageKeysEnum } from '../dictionaries/StorageKeys';

enum AlertVariant {
    InProgress = 'InProgress',
    Confirmation = 'Confirmation',
}

export const useReshardingActions = () => {
    const { t } = useTranslation();
    const { recipientsCount } = useSelector(getCampainCreatorStore);

    const getInitAlertVariant = () => {
        const inProgressVariant =
            localStorage.getItem(StorageKeysEnum.ReshardInProgressAlert) && AlertVariant.InProgress;
        const ConfirmationVariant =
            localStorage.getItem(StorageKeysEnum.ReshardConfirmationAlert) && AlertVariant.Confirmation;

        return inProgressVariant || ConfirmationVariant;
    };

    const [reshardAlert, setReshardAlert] = useState(getInitAlertVariant());
    const dispatch = useDispatch();

    const showReshardInProgressInfo = () => {
        localStorage.setItem(StorageKeysEnum.ReshardInProgressAlert, 'true');
        setReshardAlert(AlertVariant.InProgress);
    };

    const removeReshardConfirmationInfo = () => {
        localStorage.removeItem(StorageKeysEnum.ReshardConfirmationAlert);
        setReshardAlert(null);
    };
    const showReshardCompletedInfo = () => {
        localStorage.removeItem(StorageKeysEnum.ReshardInProgressAlert);
        localStorage.setItem(StorageKeysEnum.ReshardConfirmationAlert, 'true');
        setReshardAlert(AlertVariant.Confirmation);
    };

    const onReshardClick = () => {
        dispatch<any>(StartReshardContacts()).then(() => {
            showReshardInProgressInfo();
            dispatch(HideDialog());
            dispatch(
                ShowDialog({
                    body: (
                        <DialogBody variant="info" above={t('reshard_confirmation_desc')}>
                            <Trans i18nKey="reshard_confirmation_desc_info_alert" />
                        </DialogBody>
                    ),
                    title: t('confirmation'),
                    cancelLabel: t('close'),
                    onClickCancel: () => {
                        dispatch(SaveCampaignStart({ isDraft: true, redirectURL: HomePaths.Home }));
                    },
                })
            );
        });
    };

    const reshardInitiated = () => localStorage.getItem(StorageKeysEnum.ReshardInProgressAlert);

    const showReshardDialog = () => {
        dispatch(
            UpdateDialog({
                body: (
                    <SendModal recipientsCount={recipientsCount}>
                        <>
                            <Box mt="36px" />
                            <Alert
                                severity="warning"
                                body={
                                    reshardInitiated() ? (
                                        t('reshard_alert_inprogress_home_screen')
                                    ) : (
                                        <ReshardNeededAlert>
                                            <PrimaryButton text={t('takeactionnow')} onClick={onReshardClick} />
                                        </ReshardNeededAlert>
                                    )
                                }
                            />
                        </>
                    </SendModal>
                ),
                onClickCancel: () => {
                    dispatch(HideDialog());
                },

                isDisabled: true,
            })
        );
    };

    const checkReshardStatusIfNeeded = () => {
        if (!reshardInitiated()) return;

        dispatch<any>(StartPollingReshardStatus()).then(() => {
            showReshardCompletedInfo();
        });
    };

    useEffect(
        () => () => {
            dispatch(CancelReshardStatusPolling());
        },
        []
    );

    const reshardHomeAlertConfig = {
        [AlertVariant.Confirmation]: {
            severity: 'info',
            isInfo: true,
            body: t('reshard_alert_confirmation_home_screen'),
            onClose: removeReshardConfirmationInfo,
        },
        [AlertVariant.InProgress]: {
            severity: 'warning',
            isInfo: false,
            body: t('reshard_alert_inprogress_home_screen'),
        },
    }?.[reshardAlert];

    return { checkReshardStatusIfNeeded, showReshardInProgressInfo, reshardHomeAlertConfig, showReshardDialog };
};

const ReshardNeededAlert = ({ children }: PropsWithChildren<unknown>): JSX.Element => (
    <>
        <Trans i18nKey="reshard_dialog_desc" />
        <Box mb="20px" />
        {children}
    </>
);
